import React, {
  FunctionComponent, useEffect, useState, Fragment,
} from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { SelectionChangedEvent, ColDef } from '@ag-grid-enterprise/all-modules';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { RootState } from 'data/root.reducers';
import { WithAuditDetails, AuditDetails } from 'data/types';
import { SshSecurityInfoPrivileges } from 'data/sshSecurityInfo/sshSecurityInfo.types';
import * as actions from 'data/sshSecurityInfo/sshSecurityInfo.actions';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as selectors from 'data/sshSecurityInfo/sshSecurityInfo.selectors';
import {
  AuditActionType,
  AuditService,
  getAuditDetails,
  getAuditFieldInfo,
} from 'shared/userAudit/audit-constants';
import * as supportedFeaturesSelectors from 'data/supportedFeatures/supportedFeatures.selectors';
import { AppToaster } from 'shared/toaster';
import DistributorGrid from './components/SshSecurityInformationGrid';
import { sshSecurityInfoColumnDefs } from '../../SshSecurityInformation.types';

type RouterParams = {
  history: string
}

const mapState = (state: RootState) => ({
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  beforeLogout: state.authentication.beforeLogout,
  privileges: state.authentication.privileges,
  deliveryFunctionId: state.global.selectedDF,
  data: selectors.getSshSecurityInfos(state),
  selectedSshSecurityInfos: selectors.getSelectedSshSecurityInfos(state),
  userDBId: state.authentication.userDBId,
  enabledAccessFunctions: supportedFeaturesSelectors.getEnabledAccessFunction(state),
});

const mapDispatch = {
  goToSshSecurityInfoCreate: actions.goToSshSecurityInfoCreate,
  goToSshSecurityInfoEdit: actions.goToSshSecurityInfoEdit,
  goToSshSecurityInfoView: actions.goToSshSecurityInfoView,
  selectSshSecurityInfo: actions.selectSshSecurityInfo,
  getSshSecurityInfoList: actions.getSshSecurityInfoList,
  deleteSshSecurityInfo: actions.deleteSshSecurityInfo,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter

export const SshSecurityInformationList: FunctionComponent<Props> = ({
  isAuditEnabled,
  userId,
  userName,
  history,
  beforeLogout,
  privileges,
  deliveryFunctionId,
  data,
  selectedSshSecurityInfos,
  goToSshSecurityInfoCreate,
  goToSshSecurityInfoEdit,
  goToSshSecurityInfoView,
  selectSshSecurityInfo,
  getSshSecurityInfoList,
  deleteSshSecurityInfo,
}: Props) => {
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date>();
  const [columnDefs] = useState<ColDef[]>(sshSecurityInfoColumnDefs);

  useEffect(() => {
    getSshSecurityInfoList(deliveryFunctionId);
    return () => {
      AppToaster.clear();
    };
  }, [deliveryFunctionId, getSshSecurityInfoList]);

  useEffect(() => {
    setLastUpdatedAt(new Date());
  }, [data]);

  const handleRefresh = () => getSshSecurityInfoList(deliveryFunctionId);

  const handleCreateNew = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    goToSshSecurityInfoCreate(history);
  };

  const handleSelectionChanged = (event: SelectionChangedEvent) => {
    selectSshSecurityInfo(event.api.getSelectedRows());
  };

  const handleDeleteSshSecurityInfo = () => {
    const distributorConfigrationId = selectedSshSecurityInfos[0]?.id;
    if (distributorConfigrationId == null) {
      return;
    }

    const body: WithAuditDetails<void> = { data: undefined };

    if (isAuditEnabled) {
      const auditDetails = getAuditDetails(
        isAuditEnabled,
        userId,
        userName,
        getAuditFieldInfo(null),
        selectedSshSecurityInfos[0]?.id,
        AuditService.SECURITY_INFO,
        AuditActionType.SECURITY_INFO_DELETE,
      );
      body.auditDetails = auditDetails as AuditDetails;

      deleteSshSecurityInfo(deliveryFunctionId, distributorConfigrationId, body);
    }
  };

  const handleGoToEdit = () => goToSshSecurityInfoEdit(history);

  const handleGoToView = () => goToSshSecurityInfoView(history);

  const hasPrivilegeToView = privileges.includes(SshSecurityInfoPrivileges.View);
  const hasPrivilegesToCreate = privileges.includes(SshSecurityInfoPrivileges.Edit);
  const hasPrivilegeToDelete = privileges.includes(SshSecurityInfoPrivileges.Delete);

  return (
    <Fragment>
      <DistributorGrid
        data={data}
        hasPrivilegeToView={hasPrivilegeToView}
        hasPrivilegesToCreate={hasPrivilegesToCreate}
        hasPrivilegeToDelete={hasPrivilegeToDelete}
        noResults={false}
        beforeLogout={beforeLogout}
        columnDefs={columnDefs}
        lastUpdatedAt={lastUpdatedAt}
        selectedSshSecurityInfo={selectedSshSecurityInfos[0]}
        onRefresh={handleRefresh}
        onCreateNew={handleCreateNew}
        onSelectionChanged={handleSelectionChanged}
        onDelete={handleDeleteSshSecurityInfo}
        onEdit={handleGoToEdit}
        onView={handleGoToView}
      />
    </Fragment>
  );
};

export default withRouter(connector(SshSecurityInformationList));
